import { useCallback, useEffect, useRef, useState } from 'react';import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { LogOut, User as UserIcon, Zap, Gift, Share2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useGhostProtected } from '../../utils/ghostMode';
import { useI18n } from '../../contexts/I18nContext';
import { ThemeToggle } from './ThemeToggle';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

interface UserMenuProps {
  className?: string;
  isSidebar?: boolean; // Determines layout: compact row for header, full block for sidebar
  onShare?: () => void; // Sidebar only: opens the share modal owned by the page
}

export function UserMenu({ className = '', isSidebar = false, onShare }: UserMenuProps) {
 const { user, login, logout, isAdmin, isPremium } = useAuth();
 const { t, lang } = useI18n();
 // Ghost Mode ON = finestra esclusa dalla cattura: e' l'unico stato in cui si puo' mostrare
 // l'identita' reale (nome + foto) senza che finisca in un video pubblico. Vedi utils/ghostMode.ts.
 const ghostProtected = useGhostProtected();

 const [isManaging, setIsManaging] = useState(false);

 const handleManageSubscription = async (e: React.MouseEvent) => {
 e.stopPropagation();
 setIsManaging(true);
 try {
 const { apiFetch } = await import('../../utils/api');
 const res = await apiFetch('/api/stripe/customer-portal', { method: 'POST' });
 const data = await res.json();
 if (data.url) {
 window.location.href = data.url;
 } else {
 toast.error("Impossibile aprire il portale abbonamenti.");
 }
 } catch (_err) {
 toast.error("Errore di connessione al portale.");
 } finally {
 setIsManaging(false);
 }
 };

 const { data: accountStatus } = useQuery({
 queryKey: ['accountStatus'],
 queryFn: async () => {
 const { apiFetch } = await import('../../utils/api');
 const res = await apiFetch('/api/account/status');
 if (!res.ok) {
 throw new Error('Failed to fetch credits');
 }
 return await res.json();
 },
  enabled: !!user,
  staleTime: 5 * 60 * 1000
  });

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuContainerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  // Il dropdown e' portato su body: il wrapper dell'header usa overflow:clip, che
  // ritaglia le voci inferiori (click persi). Position fixed ancorata al trigger.
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null);

  const computeMenuPos = useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return null;
    const width = 224; // w-56
    const left = Math.max(8, Math.min(rect.right - width, window.innerWidth - width - 8));
    return { top: rect.bottom + 8, left };
  }, []);

  useEffect(() => {
    if (!isMenuOpen) return;
    const reposition = () => {
      const pos = computeMenuPos();
      if (pos) setMenuPos(pos);
    };
    reposition();
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (menuContainerRef.current?.contains(target)) return;
      if (dropdownRef.current?.contains(target)) return;
      setIsMenuOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setIsMenuOpen(false);
        triggerRef.current?.focus();
      }
    };
    window.addEventListener('scroll', reposition, { passive: true });
    window.addEventListener('resize', reposition);
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('scroll', reposition);
      window.removeEventListener('resize', reposition);
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isMenuOpen, computeMenuPos]);

  const toggleMenu = () => {
    if (isMenuOpen) {
      setIsMenuOpen(false);
      return;
    }
    const pos = computeMenuPos();
    if (pos) setMenuPos(pos);
    setIsMenuOpen(true);
  };


 const tokensLeft = accountStatus?.tokens_left ?? accountStatus?.credits ?? (isPremium ? Infinity : (user ? 450 : null));

 const displayUser = user ? {
 ...user,
 // Identita' reale solo quando la finestra NON e' catturabile (Ghost ON). I permessi non cambiano:
 // `isAdmin` resta vero, e il ruolo mostrato resta 'free' (nessun badge admin).
 name: isAdmin && !ghostProtected ? 'Guest' : user.name,
 role: isAdmin ? 'free' : user.role,
 avatar: isAdmin && !ghostProtected ? '' : user.avatar
 } : null;

 // Riga sotto il nome: la tua email quando l'identita' e' reale, altrimenti l'etichetta di piano
 // (Premium/GUEST) esattamente come per un utente normale.
 const showEmail = Boolean(isAdmin && ghostProtected && user?.email);
 const subtitleText = showEmail
 ? String(user?.email)
 : ((isPremium && !isAdmin) ? (t('dashboard', 'premium') || 'Premium') : (t('dashboard', 'guest') || 'GUEST'));
 const subtitleClass = showEmail
 ? 'text-text-muted normal-case'
 : `uppercase tracking-widest ${(isPremium && !isAdmin) ? 'text-brand-interactive dark:text-purple-400' : 'text-text-muted'}`;

 if (displayUser) {
 if (isSidebar) {
 return (
 <div className={`user-menu flex flex-col gap-2.5 mt-3 p-3 bg-surface-card/80 backdrop-blur-xl rounded-2xl border border-border-subtle ${className}`}>
 <div className="flex items-center gap-3">
 {displayUser.avatar ? (
 <img src={displayUser.avatar} alt={t('dashboard', 'avatar_alt') || 'Avatar'} className="w-9 h-9 rounded-full object-cover" />
 ) : (
 <div className="w-9 h-9 rounded-full bg-surface-elevated dark:bg-zinc-800 dark:bg-white/5 border border-border-subtle flex items-center justify-center">
 <UserIcon size={18} className="text-text-muted" />
 </div>
 )}
 <div className="flex-1 overflow-hidden">
 <span className="block text-sm font-bold truncate max-w-[150px] text-text-primary">
 {displayUser.name}
 </span>
 <span className={`block text-[11px] font-semibold leading-none truncate ${subtitleClass}`}>
 {subtitleText}
 </span>
 </div>
 <button
 onClick={(e) => { e.stopPropagation(); logout(); }}
 className="ml-auto shrink-0 w-8 h-8 rounded-lg flex items-center justify-center text-text-muted hover:text-red-600 dark:hover:text-red-400 hover:bg-red-500/10 transition-colors border border-border-subtle cursor-pointer"
 title={t('dashboard', 'logout') || 'Logout'}
 aria-label={t('dashboard', 'logout') || 'Logout'}
 data-testid="user-menu-logout"
 >
 <LogOut size={15} />
 </button>
 </div>

 {/* Tokens Bar */}
 {tokensLeft !== null && (
 <div className="flex flex-col gap-1">
 <div className="flex justify-between items-center text-[9px] font-bold">
 <div className="flex items-center gap-1 text-text-muted">
 <Zap size={10} />
 <span className="font-mono">{isAdmin ? '450' : tokensLeft.toLocaleString()}</span>
 </div>
 <span className="text-text-muted uppercase tracking-widest text-[8px]">{t('dashboard', 'tokens') || 'Tokens'}</span>
 </div>
 <div className="w-full h-1 bg-surface-elevated dark:bg-zinc-800 rounded-full overflow-hidden">
 <div
 className={`h-full rounded-full transition-all duration-500 bg-slate-400`}
 style={{ width: `${Math.min(100, Math.max(0, ((isAdmin ? 450 : tokensLeft) / (isPremium && !isAdmin ? 10000 : 500)) * 100))}%` }}
 />
 </div>
 </div>
 )}

 {isPremium && !isAdmin && (
 <button
 onClick={handleManageSubscription}
 disabled={isManaging}
 className="w-full bg-purple-500/10 hover:bg-purple-500/20 text-brand-interactive dark:text-purple-400 font-extrabold text-[10px] py-1.5 rounded-lg transition-colors border border-purple-500/20 uppercase tracking-widest cursor-pointer"
 >
 {isManaging ? (t('dashboard', 'loading') || 'Loading...') : (t('dashboard', 'manage_subscription') || 'Manage Subscription')}
 </button>
 )}

 <Link
 to={`/${lang}/referral`}
 className="w-full flex items-center justify-center gap-1 bg-blue-500/10 hover:bg-blue-500/20 text-blue-600 dark:text-blue-400 font-extrabold text-[10px] py-1.5 rounded-lg transition-colors border border-blue-500/20 uppercase tracking-widest cursor-pointer"
 >
 <Gift size={12} />
 {t('landing', 'referral_badge')}
 </Link>

 <div className="flex items-center gap-2">
 {onShare && (
 <button
 onClick={onShare}
  className="flex-1 flex items-center justify-center gap-1.5 h-8 px-3 rounded-xl bg-surface-card hover:bg-surface-hover text-text-primary text-[12px] font-extrabold transition-colors border border-border-subtle shadow-sm cursor-pointer"
 >
 <Share2 size={13} /> <span>{t('dashboard', 'share')}</span>
 </button>
 )}
 <ThemeToggle className="w-8 h-8 rounded-xl bg-surface-card hover:bg-surface-hover flex items-center justify-center border border-border-subtle transition-colors shadow-sm" showText={false} />
 </div>
 </div>
 );
 }

  return (
  <div ref={menuContainerRef} className={`relative inline-flex items-center shrink-0 ${className}`}>
  <button
  ref={triggerRef}
  type="button"
  onClick={toggleMenu}
  aria-haspopup="menu"
  aria-expanded={isMenuOpen}
  aria-label={displayUser.name}
  title={displayUser.name}
  className="w-9 h-9 md:w-10 md:h-10 rounded-full overflow-hidden border-2 border-border-subtle bg-surface-elevated dark:bg-zinc-800 dark:bg-white/5 flex items-center justify-center shrink-0 cursor-pointer transition-colors hover:border-border-strong focus:outline-none focus:ring-2 focus:ring-brand-interactive/40 focus:ring-offset-2 focus:ring-offset-canvas"
  >
  {displayUser.avatar ? (
  <img src={displayUser.avatar} alt={t('dashboard', 'avatar_alt') || 'Avatar'} className="w-full h-full object-cover" />
  ) : (
  <UserIcon size={18} className="text-text-muted" />
  )}
  </button>

  {isMenuOpen && menuPos && createPortal(
  <div
  ref={dropdownRef}
  role="menu"
  aria-label={displayUser.name}
  style={{ position: 'fixed', top: menuPos.top, left: menuPos.left }}
  className="animate-v6-scale-in origin-top-right w-56 rounded-2xl border border-border-subtle bg-surface-card shadow-xl z-[60] overflow-hidden"
  >
  <div className="px-4 py-3">
  <div className="text-sm font-bold text-text-primary truncate">{displayUser.name}</div>
  <div className={`text-[11px] font-semibold leading-none mt-1 truncate ${subtitleClass}`}>
  {subtitleText}
  </div>
  </div>
  <div className="h-px bg-border-subtle" />
  <Link
  role="menuitem"
  to={`/${lang}/account`}
  onClick={() => setIsMenuOpen(false)}
  className="flex items-center gap-2.5 px-4 py-2.5 text-[13px] font-semibold text-text-primary hover:bg-surface-hover transition-colors no-underline"
  >
  <UserIcon size={16} className="text-text-muted shrink-0" />
  {t('dashboard', 'menu_account') || 'Account'}
  </Link>
  <Link
  role="menuitem"
  to={`/${lang}/referral`}
  onClick={() => setIsMenuOpen(false)}
  className="flex items-center gap-2.5 px-4 py-2.5 text-[13px] font-semibold text-text-primary hover:bg-surface-hover transition-colors no-underline"
  >
  <Gift size={16} className="text-text-muted shrink-0" />
  {t('landing', 'referral_badge')}
  </Link>
  <div className="h-px bg-border-subtle" />
  <button
  role="menuitem"
  type="button"
  onClick={() => { setIsMenuOpen(false); logout(); }}
  className="w-full flex items-center gap-2.5 px-4 py-2.5 text-[13px] font-semibold text-red-600 dark:text-red-400 hover:bg-red-500/10 transition-colors cursor-pointer"
  >
  <LogOut size={16} className="shrink-0" />
  {t('dashboard', 'logout') || 'Logout'}
  </button>
  </div>,
  document.body
  )}
  </div>
  );
  }

 // Not logged in
 if (isSidebar) {
 return (
 <div
 role="button"
 tabIndex={0}
 className={`flex items-center gap-3 mt-3 p-3 bg-surface-card/80 backdrop-blur-xl rounded-2xl border border-border-subtle cursor-pointer hover:border-border-strong transition-colors ${className}`} 
 onClick={() => login('google')}
 onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') login('google') }}
 >
 <div className="w-9 h-9 rounded-full bg-surface-elevated dark:bg-zinc-800 dark:bg-white/5 flex items-center justify-center text-text-muted">
 <UserIcon size={18} />
 </div>
 <div className="flex-1 overflow-hidden">
 <div className="text-[13px] font-bold text-text-primary">{t('dashboard', 'guest') || 'Guest'}</div>
 <div className="text-[11px] text-text-muted font-semibold">{t('dashboard', 'click_to_login') || 'Click to login'}</div>
 </div>
 </div>
 );
 }

 return (
 <div className={`flex gap-2 md:gap-3 ${className}`}>
 <Link to="/login" className="no-underline shrink-0">
 <button className="min-h-[44px] px-5 py-2.5 text-[14px] md:px-6 md:py-3 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white rounded-full font-bold shadow-lg shadow-purple-500/20 transition-all flex items-center justify-center gap-2 cursor-pointer border-none shrink-0 whitespace-nowrap">
 {t('landing', 'cta_login') || 'Login'}
 </button>
 </Link>
 </div>
 );
}
